import { newsInput } from '@trafiku/contracts';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FormActions } from '@/components/form-actions';
import { ImageUpload } from '@/components/image-upload';
import { ResourceList } from '@/components/resource-list';
import { RichText } from '@/components/rich-text';
import { Field, Input, Textarea } from '@/components/ui/form';
import { Card, PageHeader, Spinner } from '@/components/ui/layout';
import { useFormState } from '@/lib/form';
import { formatDateTime } from '@/lib/format';
import { t } from '@/lib/i18n';
import { useItem, useSave } from '@/lib/resources';
import type { News } from '@/lib/types';

export function NewsPage() {
  return (
    <ResourceList<News>
      title={t('News')}
      subtitle={t("Articles in the app's information tab, newest first.")}
      resource="news"
      basePath="/news"
      newLabel={t('New article')}
      columns={[
        {
          header: t('Article'),
          cell: (n) => (
            <div className="flex items-center gap-3">
              {n.photo ? (
                <img src={n.photo} alt="" className="h-10 w-14 rounded object-cover" />
              ) : (
                <div className="h-10 w-14 rounded bg-slate-100" />
              )}
              <div>
                <div className="font-medium">{n.title}</div>
                <div className="line-clamp-1 text-xs text-slate-500">{n.short_description}</div>
              </div>
            </div>
          ),
        },
        { header: t('Published'), cell: (n) => formatDateTime(n.created_date) },
        { header: t('Edited'), cell: (n) => formatDateTime(n.updated_date) },
      ]}
    />
  );
}

export function NewsEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const item = useItem<News>('news', id);
  const save = useSave<News>('news', id);
  const navigate = useNavigate();
  const form = useFormState({ title: '', short_description: '', description: '', photo: '' });
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per item
  useEffect(() => {
    const n = item.data;
    if (n)
      form.setValues({
        title: n.title,
        short_description: n.short_description,
        description: n.description,
        photo: n.photo ?? '',
      });
  }, [item.data]);
  if (!isNew && item.isLoading) return <Spinner />;
  const { values, set, errors } = form;
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (await form.submit(newsInput, (d) => save.mutateAsync(d))) navigate('/news');
      }}
    >
      <PageHeader title={isNew ? t('New article') : t('Edit article')} back="/news" />
      <Card>
        <div className="space-y-4">
          <Field label={t('Title')} error={errors.title}>
            {(fid) => (
              <Input
                id={fid}
                value={values.title}
                onChange={(e) => set('title', e.target.value)}
                invalid={!!errors.title}
              />
            )}
          </Field>
          <Field
            label={t('Summary')}
            error={errors.short_description}
            hint={t('{n}/160 characters, shown in the list', { n: values.short_description.length })}
          >
            {(fid) => (
              <Textarea
                id={fid}
                maxLength={160}
                value={values.short_description}
                onChange={(e) => set('short_description', e.target.value)}
                invalid={!!errors.short_description}
              />
            )}
          </Field>
          <Field label={t('Photo')}>
            {() => <ImageUpload kind="newsPhotos" value={values.photo} onChange={(v) => set('photo', v)} />}
          </Field>
          <Field label={t('Article')} error={errors.description}>
            {() => (
              <RichText
                value={values.description}
                onChange={(v) => set('description', v)}
                invalid={!!errors.description}
              />
            )}
          </Field>
        </div>
        <div className="mt-6">
          <FormActions saving={save.isPending} resource="news" id={id} backTo="/news" />
        </div>
      </Card>
    </form>
  );
}
